import { ActionIcon, Loader, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Trash } from "lucide-react";
import { useState } from "react";

import {
  getGetBrandQueryKey,
  getListBrandsQueryKey,
  useCreateBrand,
  useGetBrand,
  useListBrands,
  useReplaceBrand,
} from "../../api/generated/endpoints/teams/teams";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { CentredPage } from "../../ui/CentredPage";
import type { BackLink } from "../../ui/CentredPage";
import type { PickerLibrary } from "../../platform/media/ImagePicker";
import { BrandForm } from "./BrandForm";
import { DeleteBrand } from "../../platform/brands/DeleteBrand";
import { brandDraft } from "./drafts";
import { useRefetchOnRefusal } from "../../platform/refusal";

/** A new brand on its own route: the back arrow to the brands, a saved brand opens its page. */
export function NewBrandScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/brands/new" });
  const navigate = useNavigate();
  return (
    <NewBrand
      teamId={teamId}
      back={(props) => <Link {...props} to="/teams/$teamId/brands" params={{ teamId }} />}
      onCreated={(brandId) =>
        navigate({ to: "/teams/$teamId/brands/$brandId", params: { teamId, brandId } })
      }
    />
  );
}

export interface NewBrandProps {
  teamId: string;
  /** The back arrow's link. */
  back: BackLink;
  /** Where the page goes with the brand saved. */
  onCreated: (brandId: string) => Promise<void>;
}

/**
 * A new brand, wherever it is drawn (Classic's route, or Pro's, spec
 * ui-perf/ui-switch-classic-simple-pro PR e4). The scope is read again before opening it: the
 * brand route's gate reads it.
 */
export function NewBrand({ teamId, back, onCreated }: NewBrandProps) {
  const queryClient = useQueryClient();
  const onError = useRefetchOnRefusal(getListBrandsQueryKey(teamId));
  // The new brand has no library yet: its logo comes from the team's first brand's (A5 27).
  const brands = useListBrands(teamId).data;
  const firstBrandId = brands?.[0]?.id;
  let library: PickerLibrary | null | undefined;
  if (brands !== undefined) {
    library = firstBrandId === undefined ? null : { teamId, brandId: firstBrandId };
  }
  const create = useCreateBrand({
    mutation: {
      onError,
      onSuccess: async (brand) => {
        await Promise.all([
          refreshAccessScope(queryClient),
          queryClient.invalidateQueries({ queryKey: getListBrandsQueryKey(teamId) }),
        ]);
        await onCreated(brand.id);
      },
    },
  });
  return (
    <CentredPage title="Add Brand" back={back}>
      <BrandForm
        initial={brandDraft()}
        library={library}
        editing={false}
        pending={create.isPending}
        error={create.error}
        onSubmit={(data) => create.mutate({ teamId, data })}
      />
    </CentredPage>
  );
}

/** A brand's edit on its own route: the back arrow and a save open the brand's page. */
export function EditBrandScreen() {
  const { teamId, brandId } = useParams({ from: "/signed-in/teams/$teamId/brands/$brandId/edit" });
  const navigate = useNavigate();
  return (
    <EditBrand
      teamId={teamId}
      brandId={brandId}
      back={(props) => (
        <Link {...props} to="/teams/$teamId/brands/$brandId" params={{ teamId, brandId }} />
      )}
      onSaved={() => navigate({ to: "/teams/$teamId/brands/$brandId", params: { teamId, brandId } })}
    />
  );
}

export interface EditBrandProps {
  teamId: string;
  brandId: string;
  /** The back arrow's link. */
  back: BackLink;
  /** Where the page goes with the brand saved. */
  onSaved: () => Promise<void>;
  /** Where the page goes with the brand deleted; the team's Brands page when not given. */
  onDeleted?: (() => Promise<void>) | undefined;
}

/** The whole brand, replaced on save; the form starts from what the API returned. */
export function EditBrand({ teamId, brandId, back, onSaved, onDeleted }: EditBrandProps) {
  const queryClient = useQueryClient();
  const brand = useGetBrand(teamId, brandId);
  const [deleting, setDeleting] = useState(false);
  const onError = useRefetchOnRefusal(
    getGetBrandQueryKey(teamId, brandId),
    getListBrandsQueryKey(teamId),
  );
  const replace = useReplaceBrand({
    mutation: {
      onError,
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetBrandQueryKey(teamId, brandId) }),
          queryClient.invalidateQueries({ queryKey: getListBrandsQueryKey(teamId) }),
        ]);
        await onSaved();
      },
    },
  });
  if (brand.isPending) return <Loader size="sm" aria-label="Loading the brand" />;
  if (brand.isError) return <ErrorAlert error={brand.error} />;
  return (
    <CentredPage
      title={brand.data.name}
      back={back}
      right={
        <Tooltip label="Delete">
          <ActionIcon
            variant="text"
            size={32}
            aria-label="Delete brand"
            onClick={() => setDeleting(true)}
          >
            <Trash size={16} strokeWidth={1.5} />
          </ActionIcon>
        </Tooltip>
      }
    >
      <BrandForm
        initial={brandDraft(brand.data)}
        library={{ teamId, brandId }}
        editing
        pending={replace.isPending}
        error={replace.error}
        onSubmit={(data) => replace.mutate({ teamId, brandId, data })}
      />
      {deleting ? (
        <DeleteBrand
          teamId={teamId}
          brand={brand.data}
          onCancel={() => setDeleting(false)}
          onDeleted={onDeleted}
        />
      ) : null}
    </CentredPage>
  );
}
